import { formatBytes } from '~/cynqueModules/formatBytes'

/**
 * What a case reply (Send Email on a case) may carry as attachments, checked in
 * the form so a problem shows before Send rather than as a refusal after.
 *
 * Two sources, one set of rules:
 *  - documents ALREADY on the case, sent by id (`documentIds`,
 *    cynque-api-server #1488);
 *  - files picked from the user's computer, uploaded WITH the reply as
 *    multipart `files` parts (cynque-api-server #1674, PR #1678), each stored
 *    as a new case document in the reply's own transaction.
 *
 * The server enforces every rule here too (`_read_files`, `_blocked`,
 * `_check_size` in resources/api/cases/request_handlers.py); the form is the
 * EARLY warning, so the refusal arrives before the user presses Send.
 */

/** The server's POSTMARK_MAX_MESSAGE_BYTES default (settings.py). */
export const POSTMARK_MAX_MESSAGE_BYTES = 10 * 1024 * 1024

/**
 * Postmark refuses a message carrying any of these, whatever its content. The
 * server refuses them too (a 400 naming the files), for new files and
 * `documentIds` alike. The list is #1674's, which is Postmark's.
 */
export const POSTMARK_BLOCKED_EXTENSIONS = [
  'vbs', 'exe', 'bin', 'bat', 'chm', 'com', 'cpl', 'crt', 'hlp', 'hta', 'inf',
  'ins', 'isp', 'jse', 'lnk', 'mdb', 'pcd', 'pif', 'reg', 'scr', 'sct', 'shs',
  'vbe', 'vba', 'wsf', 'wsh', 'wsl', 'msc', 'msi', 'msp', 'mst',
] // prettier-ignore

/**
 * Whether the mail provider would refuse this file, by its extension.
 *
 * ⚠ Trailing dots, spaces and tabs are trimmed FIRST, as the server's
 * `_blocked` does: Windows drops them, so `setup.exe.` and `setup.exe ` run as
 * `setup.exe` -- and read by the last dot alone they would pass here and be
 * refused by the server after Send.
 */
export function isBlockedAttachmentFilename(filename: string): boolean {
  const trimmed = filename.replace(/[. \t]+$/, '')
  const dot = trimmed.lastIndexOf('.')
  if (dot < 0) {
    return false
  }
  return POSTMARK_BLOCKED_EXTENSIONS.includes(
    trimmed.slice(dot + 1).toLowerCase()
  )
}

/**
 * The reply's size as the server measures it (`_check_size`): attachments
 * travel base64-encoded, so their raw bytes count 4/3, plus the message body's
 * UTF-8 bytes.
 */
export function encodedReplySize({
  attachmentBytes,
  body,
}: {
  attachmentBytes: number[]
  body: string
}): number {
  const raw = attachmentBytes.reduce((sum, bytes) => sum + bytes, 0)
  return Math.floor((raw * 4) / 3) + new TextEncoder().encode(body).length
}

export interface IReplyAttachment {
  filename: string
  filesize: number
}

/**
 * `true` when the attachments may go, otherwise the message to show -- the
 * shape a field's `validation.custom` returns.
 */
export function caseReplyAttachmentsProblem({
  attachments,
  body,
}: {
  attachments: IReplyAttachment[]
  body: string
}): true | string {
  const problem = attachmentFilesProblem(attachments)
  return problem === true
    ? attachmentsSizeProblem({ attachments, body })
    : problem
}

/**
 * The problem to show on ONE of a reply's attachment fields, or `true`.
 *
 * `fields` holds each field's attachments, in the form's order; `index` is the
 * field asking. Each field answers for its own files -- an empty or blocked
 * file is flagged where it was picked -- and the size, which only all of them
 * together can pass, once: on the first field that holds an attachment, as the
 * recipients' total is (emailRecipientFieldsCommon.ts). Every field running
 * caseReplyAttachmentsProblem over all of them showed each message on both.
 */
export function attachmentFieldProblem({
  fields,
  index,
  body,
}: {
  fields: IReplyAttachment[][]
  index: number
  body: string
}): true | string {
  const problem = attachmentFilesProblem(fields[index] ?? [])
  if (problem !== true) {
    return problem
  }
  if (index !== fields.findIndex((f) => f.length > 0)) {
    return true
  }
  return attachmentsSizeProblem({ attachments: fields.flat(), body })
}

/** The files the server would refuse whatever else is sent: empty, or blocked. */
function attachmentFilesProblem(
  attachments: IReplyAttachment[]
): true | string {
  // Only a file from the computer can be empty -- a case document is a stored
  // file -- but the server refuses either, so the check doesn't ask which.
  const empty = attachments
    .filter((a) => a.filesize === 0)
    .map((a) => a.filename)
  if (empty.length) {
    return (
      `${empty.join(', ')} ${empty.length === 1 ? 'is' : 'are'} empty` +
      ` -- ${empty.length === 1 ? 'it has' : 'they have'} nothing to send.`
    )
  }
  const blocked = attachments
    .map((a) => a.filename)
    .filter(isBlockedAttachmentFilename)
  if (blocked.length) {
    return (
      `Email can't carry ${blocked.join(', ')}: ` +
      `${blocked.length === 1 ? 'that file type is' : 'those file types are'}` +
      ' blocked by the mail provider.'
    )
  }
  return true
}

/** Whether the reply as a whole is over the mail provider's size limit. */
function attachmentsSizeProblem({
  attachments,
  body,
}: {
  attachments: IReplyAttachment[]
  body: string
}): true | string {
  const size = encodedReplySize({
    attachmentBytes: attachments.map((a) => a.filesize),
    body,
  })
  if (size > POSTMARK_MAX_MESSAGE_BYTES) {
    const raw = attachments.reduce((sum, a) => sum + a.filesize, 0)
    return (
      `These attachments come to ${formatBytes(raw)}, about ` +
      `${formatBytes(size)} once encoded for email -- over the ` +
      `${formatBytes(
        POSTMARK_MAX_MESSAGE_BYTES
      )} limit. Send fewer files, or ` +
      'share them another way.'
    )
  }
  return true
}

/**
 * The files a fileSelect holds. With `multiple` it is a File[]; without, a
 * single File; untouched, null. Anything that is not a File is dropped.
 */
export function pickedFiles(value: unknown): File[] {
  const values = Array.isArray(value) ? value : value ? [value] : []
  return values.filter((v): v is File => v instanceof File)
}

/**
 * The ids a multi-select holds, whether it returned ids or `{ id }` objects.
 */
export function pickedDocumentIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value
    .map((v) =>
      v && typeof v === 'object' ? (v as { id?: unknown }).id : (v as unknown)
    )
    .filter((id): id is string => typeof id === 'string')
}
